// Consolidated helper for enumerating + resetting the app's per-user UI
// preferences held in localStorage.
//
// Categories are curated so users can reset a subset (e.g. "just my sort
// preferences") without nuking their active timer or their JWT.  Anything
// not classified explicitly is treated as "other" and preserved by
// default.
//
// SAFETY: NEVER lists lextrack_token or lextrack_remember — those are
// auth state and must be cleared only via the logout flow.
//
// Per-person keys: another person's Display size on this computer
// ('lextrack_display_size:<their id>', lib/displayZoom.js, James D3
// 2026-10-29) is theirs — never listed, never cleared, not even as
// "Uncategorized".  Only the signed-in person's own key is.  The same goes
// for the Calendar height ('lextrack_calendar_height:<id>',
// lib/calendarHeight.js, feedback #844).

import { DISPLAY_SIZE_KEY, displaySizeKeyFor, getDisplaySizeUser } from './displayZoom'
import { PDF_PAGE_SETUP_KEY, pdfPageSetupKeyFor, forgetPendingPdfPageSetup } from './pdfPageSetup'
import { CALENDAR_HEIGHT_KEY, calendarHeightKeyFor } from './calendarHeight'

const AUTH_KEYS = new Set([
  'lextrack_token',
  'lextrack_remember',
  'lextrack_portal_token',
  'lextrack_portal_remember',
])

const DISPLAY_SIZE_PREFIX = `${DISPLAY_SIZE_KEY}:`
const isOwnDisplaySizeKey = (key) => {
  const uid = getDisplaySizeUser()
  return uid !== null && key === displaySizeKeyFor(uid)
}
const isOthersDisplaySizeKey = (key) => key.startsWith(DISPLAY_SIZE_PREFIX) && !isOwnDisplaySizeKey(key)
// Report PDF Margins / Table size (lib/pdfPageSetup.js) are per person too.
const PAGE_SETUP_PREFIX = `${PDF_PAGE_SETUP_KEY}:`
const isOwnPageSetupKey = (key) => {
  const uid = getDisplaySizeUser()
  return uid !== null && key === pdfPageSetupKeyFor(uid)
}
const isOthersPageSetupKey = (key) => key.startsWith(PAGE_SETUP_PREFIX) && !isOwnPageSetupKey(key)
// The Calendar's height from its drag handle (lib/calendarHeight.js), per person.
const CALENDAR_HEIGHT_PREFIX = `${CALENDAR_HEIGHT_KEY}:`
const isOwnCalendarHeightKey = (key) => {
  const uid = getDisplaySizeUser()
  return uid !== null && key === calendarHeightKeyFor(uid)
}
const isOthersCalendarHeightKey = (key) => key.startsWith(CALENDAR_HEIGHT_PREFIX) && !isOwnCalendarHeightKey(key)

// Category definitions.  Each is a predicate over a localStorage key name.
// Order matters: the FIRST match wins.
export const CATEGORIES = [
  {
    id: 'active_work',
    label: 'Active work (timer + form drafts)',
    description: 'Any running timer, in-progress form drafts.  Clearing loses unsaved work.',
    danger: true,
    // BOTH draft prefixes are load-bearing — do not "tidy" one away.  There
    // are two draft hooks and they namespace differently:
    //   useFormDraft (hooks/useFormDraft.js) — the CALLER passes the whole
    //     key, and all five callers use an underscore (lextrack_draft_matter_new,
    //     _calendar_event, _contact_new, _comm_new, _report_issue).
    //   useDraft (hooks/useDraft.js) — prepends its own KEY_PREFIX, which is
    //     'lextrack_draft:' with a COLON (lib/draftStorage.js).
    // Matching only the underscore form left every useDraft draft in the
    // "Uncategorized" bucket, so this danger-flagged reset silently spared the
    // unsaved work it promises to clear.  Guarded by uiPrefs.draftPrefix.test.js,
    // which writes through draftStorage's own writeDraft rather than a literal.
    //
    // Timer side keys belong here too (one-click timers): an owner record
    // ('lextrack_timer_owner:<slot>') or a stale command left behind after its
    // slot is cleared would otherwise sit in "Uncategorized" pointing at a
    // timer that no longer exists, and the floating bar's protocol marker is
    // re-written by the bar itself.  'lextrack_timer_owner:' does NOT start
    // with 'lextrack_timer:' (deliberately — see lib/timerSlots.js), so it
    // needs its own clause.  So do the per-user removal note
    // ('lextrack_timer_removed:<uid>' — why a timer vanished, for a form
    // open on it) and the firm's default billing status as Layout last
    // loaded it (the floating bar's one-click start reads it; Layout writes
    // it again after a clear) — lib/timerStore.js.
    match: (key) =>
      key === 'lextrack_timer' ||
      key === 'lextrack_timer_broadcast' ||
      key === 'lextrack_timer_command' ||
      key === 'lextrack_timer_bar_proto' ||
      key === 'lextrack_firm_default_billing_status' ||
      key.startsWith('lextrack_timer:') ||
      key.startsWith('lextrack_timer_owner:') ||
      key.startsWith('lextrack_timer_removed:') ||
      key.startsWith('lextrack_draft_') ||
      key.startsWith('lextrack_draft:'),
  },
  {
    id: 'sorts_filters',
    label: 'Sort + filter preferences',
    description: 'Column sort orders on tables, filter selections on Reports / TimeEntries / Discovery, the columns chosen and the PDF orientation on each report, your report PDF margins and table size, matter-detail time filter.',
    // Reports keeps three per-report keys: its filters
    // ('lextrack_reports_filters_<id>', note the plural), the PDF
    // orientation ('lextrack_report_orientation_<id>') and the Columns
    // chooser's hidden columns ('lextrack_report_columns_<id>',
    // lib/reportColumns.js).  Clearing them puts every report back on its
    // default filters, orientation and full column set.
    match: (key) =>
      key.startsWith('lextrack_sort_') ||
      key.startsWith('lextrack_te_filter_') ||
      key.startsWith('lextrack_reports_filters_') ||
      key.startsWith('lextrack_report_orientation_') ||
      key.startsWith('lextrack_report_columns_') ||
      key === 'lextrack_matter_time_filter' ||
      key === 'lextrack_matter_time_filter_custom' ||
      key === 'lextrack_report_favorites' ||
      key === 'lextrack_report_collapsed' ||
      key === 'lextrack_discovery_filters_open' ||
      isOwnPageSetupKey(key),
  },
  {
    id: 'tab_state',
    label: 'Tab + view choices',
    description: 'Which tab is active on multi-tab pages (Settings, Court Rules, Trust Compliance, etc.), which view Facts uses (list vs cards).',
    match: (key) =>
      key === 'lextrack_settings_tab' ||
      key === 'lextrack_courtrules_tab' ||
      key === 'lextrack_courtrules_deadlines_sub' ||
      key === 'lextrack_courtrules_references_sub' ||
      key === 'lextrack_trust_compliance_tab' ||
      key === 'lextrack_facts_view' ||
      key === 'lextrack_dashboard_scope' ||
      key === 'lextrack_dailies_scope' ||
      key === 'lextrack_dashboard_hidden' ||
      key === 'lextrack_ap_range' ||
      key === 'lextrack_ap_scope',
  },
  {
    id: 'layout',
    label: 'Layout + navigation',
    description: 'Sidebar collapse state (wide and medium windows), nav-group open/closed, floating timer button position, your Display size (back to Auto), your Calendar height (back to the default size).',
    // The sidebar's «/» choice is kept per window size (lib/shellLayout.js):
    // lextrack_sidebar_collapsed for wide windows, _medium for medium ones.
    // lextrack_display_size:<uid>: the signed-in person's Display size on
    // this computer (lib/displayZoom.js); absent = Auto, which the reset's
    // key-null storage event re-applies at once.  The bare
    // lextrack_display_size is the pre-D3 per-computer value (handed to the
    // first person who signs in).
    // lextrack_calendar_height:<uid>: the signed-in person's Calendar height
    // (the handle on the calendar's bottom edge, lib/calendarHeight.js);
    // absent = the default size, which an open Calendar re-reads on the
    // reset's key-null storage event.
    match: (key) =>
      key === 'lextrack_nav_collapsed' ||
      key === 'lextrack_sidebar_collapsed' ||
      key === 'lextrack_sidebar_collapsed_medium' ||
      key === 'lextrack_fab_pos' ||
      key === DISPLAY_SIZE_KEY ||
      isOwnDisplaySizeKey(key) ||
      isOwnCalendarHeightKey(key),
  },
  {
    id: 'history',
    label: 'Recent + pinned items',
    description: 'Sidebar Recent Matters / Clients / Contacts lists, Pinned Matters, search history.',
    // lextrack_quick_lists_owner: whose lists they are (lib/quickListOwner.js).
    match: (key) =>
      key === 'lextrack_recent_matters' ||
      key === 'lextrack_recent_clients' ||
      key === 'lextrack_recent_contacts' ||
      key === 'lextrack_pinned_matters' ||
      key === 'lextrack_recent_searches' ||
      key === 'lextrack_quick_lists_owner',
  },
  {
    id: 'onboarding',
    label: 'Onboarding + tour state',
    description: 'Clears the "don\'t show tour again" flag so the welcome tour reappears.',
    match: (key) =>
      key === 'lextrack_onboarding',
  },
  {
    id: 'theme',
    label: 'Theme preferences',
    description: 'Locally-cached theme choice.  Server-side theme prefs persist regardless — this just clears the initial-render cache.',
    match: (key) =>
      key === 'lextrack_theme_preference',
  },
]

// Enumerate every lextrack_ key in localStorage and bucket it into a
// category (or 'other' if nothing matches).  Skips auth keys and other
// people's Display size, report page setup and Calendar height entirely.
export function listUiPrefs () {
  const buckets = {}
  for (const c of CATEGORIES) buckets[c.id] = []
  buckets.other = []

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (!key || !key.startsWith('lextrack_')) continue
    if (AUTH_KEYS.has(key)) continue
    if (isOthersDisplaySizeKey(key)) continue
    if (isOthersPageSetupKey(key)) continue
    if (isOthersCalendarHeightKey(key)) continue

    let matched = false
    for (const c of CATEGORIES) {
      if (c.match(key)) {
        buckets[c.id].push(key)
        matched = true
        break
      }
    }
    if (!matched) buckets.other.push(key)
  }

  return buckets
}

// Clear all keys in the given category ids.  Returns the number cleared.
// If any category id is 'other', clears every unclassified key too.
export function clearUiPrefs (categoryIds) {
  const buckets = listUiPrefs()
  let cleared = 0
  for (const id of categoryIds) {
    const keys = buckets[id] || []
    for (const key of keys) {
      localStorage.removeItem(key)
      cleared++
    }
  }
  if (categoryIds.includes('sorts_filters')) forgetPendingPdfPageSetup()
  // Fire a synthetic storage event so components observing prefs update
  // in-place (Layout's Recent Matters listener, Dashboard customize state, etc.)
  try {
    window.dispatchEvent(new StorageEvent('storage', { key: null }))
  } catch { /* not all browsers dispatch synthetic StorageEvents */ }
  return cleared
}
